import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PackageCheck, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { useFloorClearanceDraft } from '@/components/wms/floorClearance/useFloorClearanceDraft';
import { BundleStickerStep } from '@/components/wms/floorClearance/BundleStickerStep';
import { PieceScanStep } from '@/components/wms/floorClearance/PieceScanStep';
import { ScannedPiecesList } from '@/components/wms/floorClearance/ScannedPiecesList';
import { FinishBundleDialog } from '@/components/wms/floorClearance/FinishBundleDialog';
import { DoneCard } from '@/components/wms/floorClearance/DoneCard';
import { ClearanceHistory } from '@/components/wms/floorClearance/ClearanceHistory';

export default function SFGFloorClearance() {
  const navigate = useNavigate();
  const { draft, sku, setBundleCode, addPiece, removePiece, reset } = useFloorClearanceDraft();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState('rebundle');

  const finish = async () => {
    setBusy(true);
    try {
      const res = await apiClient.post('/wms/sfg/floor-clearance/complete', {
        new_bundle_code: draft.bundleCode, usn_codes: draft.pieces.map((p) => p.usn_code),
      });
      setConfirmOpen(false);
      setResult(res.data);
      reset();
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not finish the bundle');
    } finally {
      setBusy(false);
    }
  };

  const discard = () => {
    if (draft.pieces.length && !window.confirm('Discard this bundle and all scanned pieces?')) return;
    reset();
  };

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/wms/sfg')} className="text-[#F9FAFB] hover:bg-white/10" data-testid="fc-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-base font-semibold">Floor Clearance</h1>
            <p className="text-xs text-gray-400">Re-bundle pieces stuck in IN_WIP and send them back to SFG Storage</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-5">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-4" data-testid="fc-tabs">
            <TabsTrigger value="rebundle" data-testid="fc-tab-rebundle">Re-bundle</TabsTrigger>
            <TabsTrigger value="history" data-testid="fc-tab-history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="rebundle" className="space-y-4">
            {result && !draft.bundleCode && (
              <DoneCard result={result} onNext={() => setResult(null)} onPutAway={() => navigate('/wms/sfg-putaway')} />
            )}
            {!(result && !draft.bundleCode) && (
              <>
                <BundleStickerStep bundleCode={draft.bundleCode} canChange={!draft.pieces.length}
                  onLocked={(c) => { setResult(null); setBundleCode(c); }} onChange={() => setBundleCode('')} />
                {draft.bundleCode && (
                  <>
                    <PieceScanStep sku={sku} scannedCodes={draft.pieces.map((p) => p.usn_code)} onPiece={addPiece} />
                    <ScannedPiecesList pieces={draft.pieces} onRemove={removePiece} />
                    <div className="flex flex-wrap justify-between gap-2">
                      <Button variant="outline" onClick={discard} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="fc-discard-btn">
                        <Trash2 className="h-4 w-4 mr-1" /> Discard
                      </Button>
                      <Button disabled={!draft.pieces.length} onClick={() => setConfirmOpen(true)}
                        className="bg-[#2D5F4F] hover:bg-[#244d40] h-11 px-6" data-testid="fc-finish-btn">
                        <PackageCheck className="h-4 w-4 mr-2" /> Finish bundle ({draft.pieces.length})
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="history">
            {tab === 'history' && <ClearanceHistory />}
          </TabsContent>
        </Tabs>
      </main>

      <FinishBundleDialog open={confirmOpen} onOpenChange={setConfirmOpen} bundleCode={draft.bundleCode}
        sku={sku} pieces={draft.pieces} busy={busy} onConfirm={finish} />
    </div>
  );
}
